import { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { AppEvent } from '../store/eventsSlice'
import { hasEventEnded } from '../utils/event'
import {
  apiRoleToUiRole,
  hasGrantedMediaConsent,
  openParticipantTicketInNewTab,
} from '../utils/participantMappers'
import { RoleBadge } from './RoleBadge'
import { Button } from './Button'
import { ConfirmationModal } from './ConfirmationModal'
import { DetailsToggleButton } from './DetailsToggleButton'
import { intersectEligibleIds } from '../utils/bulkActions'
import { TablePagination } from './TablePagination'
import { SortableColumnHeader } from './Table'
import { usePagedParticipants } from '../hooks/usePagedParticipants'
import { useBatchUpdateParticipantsMutation, type ParticipantSummary } from '@proaxiv/server-api'

// ── Icons ─────────────────────────────────────────────────────────────────────

const TrashIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
)

const TicketIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2z" />
  </svg>
)

// Same icon as `AcceptedRowActions`'s "Set as attended" button — the action is identical (manual
// admin check-in override), so it stays visually consistent across the Accepted and Unattended
// tabs rather than reinventing its own icon.
const UserCheckIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <polyline points="17 11 19 13 23 9" />
  </svg>
)

// ── Delete button ─────────────────────────────────────────────────────────────

function DeleteButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Button
      variant="unstyled"
      title={label}
      aria-label={label}
      onClick={onClick}
      className="text-pax-red hover:bg-pax-red/10 flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-lg transition-all duration-150 active:scale-90"
    >
      <TrashIcon />
    </Button>
  )
}

// ── Expanded detail row / panel ───────────────────────────────────────────────
//
// Same shape as the Details panel on every other participant tab (Registered, Accepted,
// Attended): identity fields plus an "Additional actions" section — here just the ticket link
// and the manual "Set as attended" override, both gated by `hasEventEnded`.

function DetailFields({ row }: { row: ParticipantSummary }) {
  const { t } = useTranslation()
  return (
    <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm sm:grid-cols-3">
      <div>
        <span className="text-pax-gray text-xs font-semibold uppercase">
          {t('reg_field_last_name')}
        </span>
        <p className="text-pax-black">{row.lastName || '—'}</p>
      </div>
      <div>
        <span className="text-pax-gray text-xs font-semibold uppercase">
          {t('reg_field_first_name')}
        </span>
        <p className="text-pax-black">{row.firstName}</p>
      </div>
      <div>
        <span className="text-pax-gray text-xs font-semibold uppercase">
          {t('reg_field_email')}
        </span>
        <p className="text-pax-black">{row.email}</p>
      </div>
      <div>
        <span className="text-pax-gray text-xs font-semibold uppercase">
          {t('reg_field_company')}
        </span>
        <p className="text-pax-black">{row.companyName ?? '—'}</p>
      </div>
      <div>
        <span className="text-pax-gray text-xs font-semibold uppercase">
          {t('reg_field_media')}
        </span>
        <p className="text-pax-black">
          {hasGrantedMediaConsent(row) ? t('reg_media_yes') : t('reg_media_no')}
        </p>
      </div>
      <div>
        <span className="text-pax-gray text-xs font-semibold uppercase">
          {t('reg_field_registered_via')}
        </span>
        <p className="text-pax-black">
          {row.registeredByOrganizer ? t('reg_via_organizer') : t('reg_via_self')}
        </p>
      </div>
    </div>
  )
}

function AdditionalActions({
  canMarkAttended,
  onMarkAttended,
  onOpenTicket,
}: {
  canMarkAttended: boolean
  onMarkAttended: () => void
  onOpenTicket: () => void
}) {
  const { t } = useTranslation()
  return (
    <div className="mt-4">
      <span className="text-pax-gray text-xs font-semibold uppercase">
        {t('acc_additional_actions')}
      </span>
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        {canMarkAttended && (
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<UserCheckIcon />}
            onClick={onMarkAttended}
          >
            {t('acc_mark_attended')}
          </Button>
        )}
        <Button variant="secondary" size="sm" leftIcon={<TicketIcon />} onClick={onOpenTicket}>
          {t('acc_ticket_link')}
        </Button>
      </div>
    </div>
  )
}

function ExpandedRow({
  row,
  colSpan,
  canMarkAttended,
  onMarkAttended,
  onOpenTicket,
}: {
  row: ParticipantSummary
  colSpan: number
  canMarkAttended: boolean
  onMarkAttended: () => void
  onOpenTicket: () => void
}) {
  return (
    <tr className="bg-pax-light-purple/20">
      <td colSpan={colSpan} className="px-6 py-4">
        <DetailFields row={row} />
        <AdditionalActions
          canMarkAttended={canMarkAttended}
          onMarkAttended={onMarkAttended}
          onOpenTicket={onOpenTicket}
        />
      </td>
    </tr>
  )
}

interface BatchUpdateResult {
  success?: boolean
  successCount?: number
  failedIds?: string[]
}

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
  event: AppEvent
}

/**
 * Accepted participants who did not check in. Only surfaced once the event has ended —
 * before that, the same {@code ACCEPTED} pool is shown as pending RSVPs on the
 * {@link AcceptedParticipantsTab}. Certificates of Attendance are never eligible here
 * (see EP-13 / EP-11: only the Attended table can generate/send a CoA).
 *
 * <p>Supports a post-event manual attendance override: organisers can bulk-select rows and
 * mark them attended ({@code MARK_ATTENDED}), pinning {@code attendedAt} to the event start.
 */
export function UnattendedParticipantsTab({ event }: Props) {
  const { t } = useTranslation()

  const {
    participants: rows,
    total,
    offset,
    take,
    sort,
    order,
    setOffset,
    setTake,
    toggleSort,
  } = usePagedParticipants({
    eventId: event.id,
    status: 'ACCEPTED',
    defaultSort: 'lastName',
    defaultOrder: 'asc',
  })

  const [batchUpdate, { isLoading: batchLoading }] = useBatchUpdateParticipantsMutation()
  const [confirmDelete, setConfirmDelete] = useState<string[] | null>(null)
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set())
  const [markError, setMarkError] = useState<string | null>(null)

  const eventHasEnded = hasEventEnded(event)
  const allSelected = rows.length > 0 && selectedKeys.size === rows.length
  const someSelected = selectedKeys.size > 0 && !allSelected
  const selectedCount = selectedKeys.size

  // Same intersection pattern as every other participant tab's bulk toolbar (see
  // `intersectEligibleIds`): "Set as attended" is gated on the event having ended (a
  // row-independent condition here since every row is already ACCEPTED-but-not-checked-in), but
  // routed through the shared helper for consistency with the per-row-varying actions elsewhere.
  // The per-row action in the expanded Details panel reuses the exact same predicate through the
  // same helper (just for a one-row "selection"), so a single source of truth decides eligibility
  // everywhere it's checked.
  const selectedRows = rows.filter((r) => selectedKeys.has(r.id!))
  const getRowId = (r: ParticipantSummary) => r.id!
  const canMarkAttendedRow = () => eventHasEnded
  const bulkMarkAttendedIds = intersectEligibleIds(selectedRows, getRowId, canMarkAttendedRow)
  const bulkDeleteIds = intersectEligibleIds(selectedRows, getRowId, () => true)

  function handleSelectRow(key: string, checked: boolean) {
    setSelectedKeys((prev) => {
      const next = new Set(prev)
      if (checked) next.add(key)
      else next.delete(key)
      return next
    })
  }

  function handleSelectAll(checked: boolean) {
    setSelectedKeys(checked ? new Set(rows.map((r) => r.id!)) : new Set())
  }

  function toggleExpand(id: string) {
    setExpandedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  async function handleConfirmDelete() {
    if (!confirmDelete) return
    await batchUpdate({
      eventId: event.id,
      batchBody: {
        participantIds: confirmDelete,
        action: 'DELETE',
        sendEmailNotification: false,
      },
    })
    setSelectedKeys((prev) => {
      const next = new Set(prev)
      confirmDelete.forEach((id) => next.delete(id))
      return next
    })
    setConfirmDelete(null)
  }

  async function handleMarkAttended(ids: string[]) {
    if (ids.length === 0) return
    setMarkError(null)
    try {
      const response = (await batchUpdate({
        eventId: event.id,
        batchBody: {
          participantIds: ids,
          action: 'MARK_ATTENDED',
          sendEmailNotification: false,
        },
      }).unwrap()) as BatchUpdateResult
      const failed = response.failedIds?.length ?? 0
      if (failed > 0) {
        setMarkError(t('att_set_error_partial', { count: failed }))
      }
    } catch {
      setMarkError(t('att_set_error'))
    } finally {
      setSelectedKeys(new Set())
    }
  }

  async function handleViewTicket(row: ParticipantSummary) {
    if (!row.id) return
    await openParticipantTicketInNewTab(row.id)
  }

  return (
    <div className="flex flex-col gap-4">
      {markError && (
        <p className="border-pax-red/20 bg-pax-red/5 self-start rounded-lg border px-3 py-1.5 text-xs text-red-600">
          {markError}
        </p>
      )}

      <TablePagination
        total={total}
        offset={offset}
        take={take}
        onOffsetChange={setOffset}
        onTakeChange={setTake}
        toolbarTestId="unattended-toolbar"
        selectionActions={
          selectedCount > 0 ? (
            <>
              <span className="text-pax-gray text-xs font-medium">
                {t('n_selected', { count: selectedCount })}
              </span>
              {bulkMarkAttendedIds.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<UserCheckIcon />}
                  disabled={batchLoading}
                  onClick={() => void handleMarkAttended(bulkMarkAttendedIds)}
                >
                  {t('acc_mark_attended')}
                </Button>
              )}
              <Button
                variant="danger"
                size="sm"
                leftIcon={<TrashIcon />}
                onClick={() => setConfirmDelete(bulkDeleteIds)}
                className="cursor-pointer"
              >
                {t('reg_delete')}
              </Button>
            </>
          ) : undefined
        }
      >
        <div data-testid="mobile-view" className="flex flex-col gap-3 md:hidden">
          {rows.length === 0 ? (
            <p className="text-pax-gray py-6 text-center text-sm">{t('att_empty_nonattended')}</p>
          ) : (
            rows.map((row) => {
              const isSelected = selectedKeys.has(row.id!)
              const isExpanded = expandedKeys.has(row.id!)
              const rowMarkAttendedEligible =
                intersectEligibleIds([row], getRowId, canMarkAttendedRow).length > 0
              return (
                <div
                  key={row.id}
                  className="border-pax-purple/20 rounded-2xl border bg-white p-4 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2.5">
                      <input
                        type="checkbox"
                        aria-label={t('table_select_row', { name: row.email })}
                        checked={isSelected}
                        onChange={(e) => handleSelectRow(row.id!, e.target.checked)}
                        className="accent-pax-blue mt-0.5 h-4 w-4 cursor-pointer rounded"
                      />
                      <div>
                        <p className="text-pax-black font-medium">{row.email}</p>
                        <RoleBadge role={apiRoleToUiRole(row.role)} className="mt-1.5" />
                      </div>
                    </div>
                    <DeleteButton
                      label={t('reg_delete')}
                      onClick={() => setConfirmDelete([row.id!])}
                    />
                  </div>

                  {isExpanded && (
                    <div className="border-pax-purple/20 mt-3 rounded-xl border bg-slate-50 p-3 text-xs">
                      <DetailFields row={row} />
                      <AdditionalActions
                        canMarkAttended={rowMarkAttendedEligible}
                        onMarkAttended={() => void handleMarkAttended([row.id!])}
                        onOpenTicket={() => void handleViewTicket(row)}
                      />
                    </div>
                  )}

                  <DetailsToggleButton
                    variant="block"
                    expanded={isExpanded}
                    onClick={() => toggleExpand(row.id!)}
                    className="mt-3"
                  />
                </div>
              )
            })
          )}
        </div>

        {/* Desktop table */}
        <div data-testid="desktop-view" className="hidden md:block">
          <div className="border-pax-purple/30 overflow-hidden rounded-2xl border bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-pax-purple/20 bg-pax-light-purple/60 border-b">
                    <th className="w-10 px-4 py-3">
                      <input
                        type="checkbox"
                        aria-label={t('select_all')}
                        checked={allSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = someSelected
                        }}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="accent-pax-blue h-4 w-4 cursor-pointer rounded"
                      />
                    </th>
                    <th className="text-pax-gray px-4 py-3 text-left text-xs font-semibold tracking-wider">
                      <SortableColumnHeader
                        label={t('acc_col_email')}
                        field="email"
                        activeField={sort}
                        order={order}
                        onSort={toggleSort}
                      />
                    </th>
                    <th className="text-pax-gray px-4 py-3 text-left text-xs font-semibold tracking-wider">
                      {t('acc_col_role')}
                    </th>
                    {/* Header left visually blank (#274); aria-label keeps a real accessible name. */}
                    <th
                      aria-label={t('reg_col_actions')}
                      className="text-pax-gray px-4 py-3 text-right text-xs font-semibold tracking-wider"
                    ></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-pax-gray px-4 py-10 text-center text-sm">
                        {t('att_empty_nonattended')}
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => {
                      const isSelected = selectedKeys.has(row.id!)
                      const isExpanded = expandedKeys.has(row.id!)
                      const rowMarkAttendedEligible =
                        intersectEligibleIds([row], getRowId, canMarkAttendedRow).length > 0
                      return (
                        <Fragment key={row.id}>
                          <tr
                            className={[
                              'border-pax-purple/10 border-b transition-colors last:border-0',
                              isSelected ? 'bg-pax-light-blue/40' : 'hover:bg-pax-light-purple/40',
                            ].join(' ')}
                          >
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                aria-label={t('table_select_row', { name: row.email })}
                                checked={isSelected}
                                onChange={(e) => handleSelectRow(row.id!, e.target.checked)}
                                className="accent-pax-blue h-4 w-4 cursor-pointer rounded"
                              />
                            </td>
                            <td className="text-pax-black px-4 py-3 font-medium">{row.email}</td>
                            <td className="px-4 py-3">
                              <RoleBadge role={apiRoleToUiRole(row.role)} />
                            </td>
                            <td className="px-2 py-2">
                              <div className="flex justify-end gap-0.5">
                                <DetailsToggleButton
                                  expanded={isExpanded}
                                  onClick={() => toggleExpand(row.id!)}
                                />
                                <DeleteButton
                                  label={t('reg_delete')}
                                  onClick={() => setConfirmDelete([row.id!])}
                                />
                              </div>
                            </td>
                          </tr>
                          {isExpanded && (
                            <ExpandedRow
                              row={row}
                              colSpan={4}
                              canMarkAttended={rowMarkAttendedEligible}
                              onMarkAttended={() => void handleMarkAttended([row.id!])}
                              onOpenTicket={() => void handleViewTicket(row)}
                            />
                          )}
                        </Fragment>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </TablePagination>

      {confirmDelete && (
        <ConfirmationModal
          title={t('reg_delete_confirm', { count: confirmDelete.length })}
          notify={false}
          showNotifyCheckbox={false}
          onConfirm={() => void handleConfirmDelete()}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  )
}
